import { Card, CardButtonActionMode, HeaderGradient, MiniCard, Row } from '@app/components';
import React, { memo, Fragment } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { StyleSheet } from 'react-native';
import { ICustomer, convertToEuros } from '@app/common';
import { UseFormReturn, useWatch } from 'react-hook-form';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { SlantTitle } from '@app/components/typography';
import { SvgUsersRound } from '@app/components/icons/UsersRound';
import { DateTime } from 'luxon';
import * as R from 'remeda';
import { formatPhone } from '@tickeat/intl-phone-number';

type CustomerCardProps = {
  toggle: (mode: CardButtonActionMode) => void;
  customer?: Partial<ICustomer>;
  formMethods: UseFormReturn<any, any, any>;
};

const formatCardData = (partialCustomer: Partial<ICustomer>) => {
  const { firstname, lastname } = partialCustomer;

  if (firstname && lastname) {
    return { name: `${firstname} ${lastname}` };
  }

  if (firstname) {
    return { name: firstname };
  }

  if (lastname) {
    return { name: lastname };
  }

  return { name: 'Nouveau client' };
}

export { formatPhone };

export const CustomerCard = memo(({ formMethods, toggle, customer }: CustomerCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const cardData = useWatch({
    control: formMethods.control,
  });

  const { name } = formatCardData(cardData);

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Fiche client</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>{name}</Card.Title>
                {customer?._id && <Card.Subtitle>Ref: {customer._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              <Card.ButtonAction onPress={toggle} permissionScope="customers" />
            </Row>
          </Card.Header>
        </Card.Content>

        {!R.isEmptyish(customer) && (
        <MiniCard>
            <MiniCard.Tabs>
              <MiniCard.TabsHolder>
                <MiniCard.Tab Icon={SvgUsersRound} isActive />
              </MiniCard.TabsHolder>
            </MiniCard.Tabs>
            <MiniCard.Container>
              <MiniCard.Content extraStyle={{ paddingLeft: 60, paddingRight: 60, paddingTop: 24 }}>

                <Card.LineBreak height={16} />

                <SlantTitle label="Informations" color={theme.colors.gold} />

                <Card.HelpScrollContainer extraStyle={{ paddingLeft: 24, paddingRight: 24, paddingTop: 60, paddingBottom: 90 }}>

                  {/* Informations de contact */}
                  <InfoSection>
                    <SectionTitle>Contact</SectionTitle>
                    <InfoGrid>
                      {customer.email && (
                        <InfoRow>
                          <InfoLabel>Email</InfoLabel>
                          <InfoValue>{customer.email}</InfoValue>
                        </InfoRow>
                      )}
                      {customer.phone && (
                        <InfoRow>
                          <InfoLabel>Téléphone</InfoLabel>
                          <InfoValue>{formatPhone(customer.phone)}</InfoValue>
                        </InfoRow>
                      )}
                      {customer.birthday && (
                        <InfoRow>
                          <InfoLabel>Date de naissance</InfoLabel>
                          <InfoValue>
                            {DateTime.fromISO(customer.birthday).toFormat('dd/MM/yyyy')}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {customer.birthday && (
                        <InfoRow>
                          <InfoLabel>Age</InfoLabel>
                          <InfoValue>
                            {Math.floor(DateTime.now().diff(DateTime.fromISO(customer.birthday), 'years').years)} ans
                          </InfoValue>
                        </InfoRow>
                      )}
                    </InfoGrid>
                  </InfoSection>


                  {/* Préférences */}
                  <InfoSection>
                    <SectionTitle>Préférences</SectionTitle>
                    <PreferencesGrid>
                      <PreferenceItem>
                        <PreferenceIcon active={customer.optInEmail}>
                          {customer.optInEmail ? '✅' : '❌'}
                        </PreferenceIcon>
                        <PreferenceLabel>Newsletter</PreferenceLabel>
                      </PreferenceItem>
                      <PreferenceItem>
                        <PreferenceIcon active={customer.optInSMS}>
                          {customer.optInSMS ? '✅' : '❌'}
                        </PreferenceIcon>
                        <PreferenceLabel>SMS</PreferenceLabel>
                      </PreferenceItem>
                      <PreferenceItem>
                        <PreferenceIcon active={customer.optInLoyalty}>
                          {customer.optInLoyalty ? '✅' : '❌'}
                        </PreferenceIcon>
                        <PreferenceLabel>Programme fidélité</PreferenceLabel>
                      </PreferenceItem>
                    </PreferencesGrid>
                  </InfoSection>

                  <Card.LineBreak height={24} />

                </Card.HelpScrollContainer>
              </MiniCard.Content>
            </MiniCard.Container>
        </MiniCard>
        )}
      </Card.Container>
    </Card>
  );
});

const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});

// Styled Components
const InfoSection = styled.View({
  marginBottom: normalize(28),
});

const SectionTitle = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(18),
  fontWeight: 'bold',
  marginBottom: normalize(16),
  letterSpacing: 0.3,
});

const InfoGrid = styled.View({
  gap: normalize(12),
  backgroundColor: 'rgba(253, 255, 252, 0.03)',
  borderRadius: normalize(12),
  padding: normalize(16),
  borderWidth: 1,
  borderColor: 'rgba(253, 255, 252, 0.08)',
});

const InfoRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: normalize(8),
});

const InfoLabel = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(14),
  fontWeight: '500',
  flex: 1,
});

const InfoValue = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(15),
  fontWeight: '600',
  flex: 1,
  textAlign: 'right',
});

// Preferences Components
const PreferencesGrid = styled.View({
  flexDirection: 'row',
  gap: normalize(12),
  flexWrap: 'wrap',
});

const PreferenceItem = styled.View({
  flex: 1,
  minWidth: normalize(140),
  backgroundColor: 'rgba(253, 255, 252, 0.03)',
  borderRadius: normalize(12),
  padding: normalize(16),
  borderWidth: 1,
  borderColor: 'rgba(253, 255, 252, 0.08)',
  alignItems: 'center',
  gap: normalize(8),
});

const PreferenceIcon = styled.Text<{ active?: boolean }>(({ props }) => ({
  fontSize: normalize(32),
  opacity: props.active ? 1 : 0.5,
}));

const PreferenceLabel = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(13),
  fontWeight: '500',
  textAlign: 'center',
});
